import { Filter } from 'lucide-react'
import { Button, Dropdown, SearchInput } from '@/components/ui'
import { PLATFORM_LABEL } from '@/internationalization/constants'
import { useT } from '@/internationalization/i18n'
import { PatronFilter } from '@/features/patrons'
import type { MeetingListParams } from '../api'
import { STATUSES, currentLeaveReasonOf, isStatusOptionSelected, statusOptionPatch } from './meetingStatusOptions'

// Platform names are proper nouns and are not translated.
const PLATFORMS = [
  { value: 'all', key: 'meetings.platform.all', label: null as string | null },
  { value: 'teams', key: null, label: PLATFORM_LABEL.teams },
  { value: 'meet', key: null, label: PLATFORM_LABEL.meet },
  { value: 'zoom', key: null, label: PLATFORM_LABEL.zoom },
  { value: 'device', key: null, label: PLATFORM_LABEL.device },
]

/**
 * Search plus three filters. On mobile the search field takes the full width and
 * the filters sit under it — a filter bar that scrolls sideways hides the
 * filters that are switched on.
 *
 * Filter state lives in the URL (see MeetingsPage), so a filtered list is a
 * shareable link and the back button undoes a filter.
 */
export function MeetingFilters({
  params,
  onChange,
  onReset,
}: {
  params: MeetingListParams
  onChange: (patch: Partial<MeetingListParams>) => void
  onReset: () => void
}) {
  const t = useT()
  const currentLeaveReason = currentLeaveReasonOf(params)
  const statusLabel = (value: string, leaveReason?: string) => {
    // A URL can carry a leaveReason with no status (a stale bookmark, a hand
    // edit), and no row matches that pair -- so fall back to the status row
    // alone rather than printing the raw param, which renders the button as
    // the literal word "all".
    const s =
      STATUSES.find((x) => x.value === value && x.leaveReason === leaveReason) ??
      STATUSES.find((x) => x.value === value && x.leaveReason === undefined)
    return s ? t(s.key) : value
  }
  const platformLabel = (value: string) => {
    const p = PLATFORMS.find((x) => x.value === value)
    return p ? (p.key ? t(p.key) : p.label) : value
  }
  const hasFilters =
    Boolean(params.q) ||
    (params.status ?? 'all') !== 'all' ||
    currentLeaveReason !== undefined ||
    (params.platform ?? 'all') !== 'all' ||
    (params.patron ?? 'all') !== 'all'

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <SearchInput
        value={params.q ?? ''}
        onChange={(q) => onChange({ q, page: 1 })}
        placeholder={t('meetings.searchPlaceholder')}
        className="sm:max-w-sm"
        aria-label={t('meetings.searchAria')}
      />
      <div className="flex items-center gap-2">
        <Dropdown
          align="start"
          heading={t('meetings.filter.status')}
          trigger={({ toggle }) => (
            <Button size="md" leftIcon={<Filter className="h-4 w-4" />} onClick={toggle}>
              {statusLabel(params.status ?? 'all', currentLeaveReason)}
            </Button>
          )}
          items={STATUSES.map((status) => ({
            id: status.id,
            label: t(status.key),
            selected: isStatusOptionSelected(status, params),
            onSelect: () => onChange(statusOptionPatch(status)),
          }))}
        />
        <Dropdown
          align="start"
          heading={t('meetings.filter.platform')}
          trigger={({ toggle }) => (
            <Button size="md" onClick={toggle}>
              {platformLabel(params.platform ?? 'all')}
            </Button>
          )}
          items={PLATFORMS.map((platform) => ({
            id: platform.value,
            label: platform.key ? t(platform.key) : (platform.label as string),
            selected: (params.platform ?? 'all') === platform.value,
            onSelect: () => onChange({ platform: platform.value, page: 1 }),
          }))}
        />
        <PatronFilter
          value={params.patron ?? 'all'}
          onChange={(patron) => onChange({ patron, page: 1 })}
        />
        {hasFilters && (
          <Button variant="ghost" size="md" onClick={onReset}>
            {t('common.clear')}
          </Button>
        )}
      </div>
    </div>
  )
}
